import type { ReactNode } from "react";

import { Icon } from "@iconify-icon/react";

import { cn } from "@/lib/utils";

interface SettingsCardProps {
  title?: string | ReactNode;
  description?: string;
  icon?: string;
  children?: ReactNode;
  action?: ReactNode;
  className?: string;
  bottomCircle?: string;
  topCircle?: string;
  topCirclePlacement?: "top-left" | "top-right";
  bottomCirclePlacement?: "bottom-left" | "bottom-right";
  topCircleSize?: string;
  bottomCircleSize?: string;
}

export function SettingsCard({
  title,
  description,
  icon,
  children,
  action,
  className,
  bottomCircle = "from-pink-500 to-teal-600",
  topCircle = "from-cyan-600 to-purple-400",
  topCirclePlacement = "top-left",
  bottomCirclePlacement = "bottom-right",
  topCircleSize = "h-24 w-24",
  bottomCircleSize = "h-32 w-32",
}: SettingsCardProps) {
  // Calculate positioning based on circle size
  const getCirclePosition = (placement: string, size: string) => {
    const sizeValue = parseInt(size.match(/h-(\d+)/)?.[1] || "24");
    const offset = Math.floor(sizeValue / 2);

    if (placement === "top-left") return `-top-${offset} -left-${offset}`;
    if (placement === "top-right") return `-top-${offset} -right-${offset}`;
    if (placement === "bottom-left") return `-bottom-${offset} -left-${offset}`;
    if (placement === "bottom-right") return `-bottom-${offset} -right-${offset}`;

    return `-top-${offset} -left-${offset}`;
  };

  const topPosition = getCirclePosition(topCirclePlacement, topCircleSize);
  const bottomPosition = getCirclePosition(bottomCirclePlacement, bottomCircleSize);

  return (
    <div
      className={cn(
        // Medium padding and border radius
        "bg-foreground/50 text-accent-foreground relative z-50 rounded-xl border-none p-5 shadow-lg backdrop-blur-xl",
        className,
      )}
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 overflow-hidden rounded-2xl opacity-5">
        {/* Dynamic gradient circles with configurable placement and size */}
        <div
          className={`absolute ${topPosition} ${topCircleSize} rounded-full bg-gradient-to-br ${topCircle}`}
        />
        <div
          className={`absolute ${bottomPosition} ${bottomCircleSize} rounded-full bg-gradient-to-tr ${bottomCircle}`}
        />
      </div>

      <div className="relative">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            {icon && (
              <div className="bg-muted/20 flex items-center justify-center rounded-xl p-2 backdrop-blur-sm">
                <Icon
                  className="text-accent-foreground"
                  height={"1.5rem"}
                  icon={icon}
                  width={"1.5rem"}
                />
              </div>
            )}

            <div className={"flex flex-col justify-center"}>
              <h3 className="text-accent-foreground text-base font-semibold">{title}</h3>
              {description && <p className="text-muted-foreground text-sm">{description}</p>}
            </div>
          </div>
          {action && <div className="my-auto flex-shrink-0 text-sm">{action}</div>}
        </div>
        {children && <div className="mt-3.5 text-sm">{children}</div>}
      </div>
    </div>
  );
}
